import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { ArrowDown, CheckCircle2 } from 'lucide-react';
export function HeroSection() {
  const scrollToProblem = () => {
    const target = document.getElementById('problem');
    if (!target) return;
    const targetPosition = target.getBoundingClientRect().top + window.scrollY;
    const startPosition = window.scrollY;
    const distance = targetPosition - startPosition;
    const duration = 2000;
    let startTime: number | null = null;
    function animation(currentTime: number) {
      if (startTime === null) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const ease = progress < 0.5 ? 4 * progress * progress * progress : 1 - Math.pow(-2 * progress + 2, 3) / 2;
      window.scrollTo(0, startPosition + distance * ease);
      if (progress < 1) {
        requestAnimationFrame(animation);
      }
    }
    requestAnimationFrame(animation);
  };
  const scrollToOffer = () => {
    const target = document.getElementById('offer');
    if (!target) return;
    const targetPosition = target.getBoundingClientRect().top + window.scrollY;
    const startPosition = window.scrollY;
    const distance = targetPosition - startPosition;
    const duration = 2000; // 2 seconds for a slow, deliberate scroll
    let startTime: number | null = null;
    function animation(currentTime: number) {
      if (startTime === null) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-in-out cubic for smooth deceleration
      const ease = progress < 0.5 ? 4 * progress * progress * progress : 1 - Math.pow(-2 * progress + 2, 3) / 2;
      window.scrollTo(0, startPosition + distance * ease);
      if (progress < 1) {
        requestAnimationFrame(animation);
      }
    }
    requestAnimationFrame(animation);
  };
  return <section className="relative min-h-screen flex flex-col justify-center pt-20 pb-16 px-4 sm:px-6 lg:px-8 bg-slate-950 overflow-hidden" data-id="element-110">
      {/* Background texture/gradient overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-slate-800 via-slate-950 to-slate-950 opacity-50 z-0" data-id="element-111"></div>

      <div className="max-w-7xl mx-auto w-full relative z-10 grid lg:grid-cols-2 gap-12 items-center" data-id="element-112">
        {/* Left Column: Copy */}
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.6
      }} className="flex flex-col gap-6" data-id="element-113">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-orange/10 border border-brand-orange/20 w-fit" data-id="element-114">
            <span className="w-2 h-2 rounded-full bg-brand-orange animate-pulse" data-id="element-115"></span>
            <span className="text-brand-orange text-xs font-bold uppercase tracking-widest" data-id="element-116">
              For Men 50+ Only
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-heading font-bold text-white leading-[0.95] uppercase" data-id="element-117">
            Stop Paying the{' '}
            <span className="text-brand-orange" data-id="element-118">'Knee Tax'</span>
          </h1>

          <p className="text-xl sm:text-2xl text-slate-300 font-light leading-relaxed max-w-2xl" data-id="element-119">
            How Men 50+ are Torching Belly Fat Without a Single Minute of
            Running.
          </p>

          <div className="flex flex-col gap-3 mt-2" data-id="element-120">
            {['Protect your meniscus while burning fat', "Build 'Muscle Armor' to support aging joints", 'Reset metabolism without high-impact cardio'].map((item, i) => <div key={i} className="flex items-center gap-3" data-id="element-121">
                <CheckCircle2 className="text-brand-orange w-5 h-5 flex-shrink-0" data-id="element-122" />
                <span className="text-slate-300" data-id="element-123">{item}</span>
              </div>)}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mt-8" data-id="element-124">
            <Button size="xl" onClick={scrollToOffer} className="w-full sm:w-auto text-lg" data-id="element-125">
              Get The System — $6.36
            </Button>
            <Button variant="outline" size="xl" onClick={scrollToOffer} className="w-full sm:w-auto text-lg" data-id="element-126">
              How It Works
            </Button>
          </div>

          <p className="text-xs text-slate-500 mt-4 uppercase tracking-wide" data-id="element-127">
            Instant Digital Download • 30-Day Money Back Guarantee
          </p>
        </motion.div>

        {/* Right Column: Image */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="relative mx-auto lg:ml-auto" data-id="element-128">
          <div className="relative z-10 rounded-xl overflow-hidden shadow-2xl shadow-brand-orange/10 border border-slate-800 bg-slate-900 max-w-md mx-auto transform rotate-1 hover:rotate-0 transition-transform duration-500" data-id="element-129">
            <img src="https://cdn.magicpatterns.com/uploads/qNh6q87kggBM7qTx8h92yA/heylloyd-image-1771365135122.jpg" alt="The No-Knee Tax Ladder Guide on Tablet" className="w-full h-auto object-cover" data-id="element-130" />

            {/* Badge overlay */}
            <div className="absolute -bottom-6 -right-6 bg-brand-orange text-white w-24 h-24 rounded-full flex flex-col items-center justify-center shadow-lg transform rotate-12 border-4 border-slate-950 z-20" data-id="element-131">
              <span className="text-xs font-bold uppercase" data-id="element-132">Only</span>
              <span className="font-heading font-bold text-[20px]" data-id="element-133">$6.36</span>
            </div>
          </div>

          {/* Decorative elements behind image */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-brand-orange/5 blur-3xl rounded-full -z-10" data-id="element-134"></div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1,
      duration: 1
    }} className="absolute bottom-8 left-1/2 -translate-x-1/2 text-slate-500 flex flex-col items-center gap-2 cursor-pointer" onClick={scrollToProblem} data-id="element-135">
        <span className="text-xs uppercase tracking-widest" data-id="element-136">The Problem</span>
        <ArrowDown className="w-5 h-5 animate-bounce" data-id="element-137" />
      </motion.div>
    </section>;
}